import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Mail, Lock, User, ArrowLeft, Camera, Image as ImageIcon, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { LOGO_DARK_BG, LOGO_LIGHT_BG } from '../../lib/constants';

export default function AuthPage({ mode = 'login' }) {
  const [isRegister, setIsRegister] = useState(mode === 'register');
  const [form, setForm] = useState({ name: '', email: '', password: '', whatsapp: '', city: '', picture: '' });
  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('El archivo debe ser una imagen');
      return;
    }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data?.url) setForm((prev) => ({ ...prev, picture: data.url }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo subir la foto');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const redirectByRole = (user) => {
    navigate('/app', { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isRegister && !form.picture) {
      toast.error('Agrega una foto de perfil para completar tu registro');
      return;
    }
    setLoading(true);
    try {
      if (isRegister) {
        const result = await register({
          name: form.name, email: form.email, password: form.password,
          whatsapp: form.whatsapp || null, city: form.city || null, picture: form.picture,
        });
        if (result?.pending) {
          toast.success('Registro recibido');
          navigate('/registro/pendiente', { replace: true, state: { email: form.email, message: result.message } });
          return;
        }
        toast.success('Cuenta creada. ¡Bienvenido a CNIB Connect!');
        redirectByRole(result);
      } else {
        const user = await login(form.email, form.password);
        toast.success('Sesión iniciada.');
        redirectByRole(user);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo completar la operación.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pl-11 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15';

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="hero-grain relative hidden flex-col justify-between bg-cnib-navy p-12 lg:flex">
        <Link to="/" data-testid="auth-back-home">
          <img src={LOGO_DARK_BG} alt="CNIB" className="h-12 w-auto" />
        </Link>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-orange-500">CNIB Connect</p>
          <h1 className="mt-4 font-heading text-4xl font-extrabold leading-tight text-white xl:text-5xl">
            Tu mundo inmobiliario,
            <br />
            <span className="text-orange-500">en un solo lugar.</span>
          </h1>
          <p className="mt-5 max-w-md leading-relaxed text-slate-300">
            Accede a tu credencial digital, feed profesional, directorio de socios y todas las herramientas de la red
            inmobiliaria más grande de Bolivia.
          </p>
        </div>
        <p className="text-xs text-slate-500">© 2026 Cámara Nacional Inmobiliaria de Bolivia</p>
      </div>

      <div className="flex items-center justify-center bg-slate-50 px-4 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors hover:text-cnib-blue lg:hidden">
            <ArrowLeft className="h-4 w-4" /> Volver al inicio
          </Link>
          <div className="mb-8 lg:hidden">
            <img src={LOGO_LIGHT_BG} alt="CNIB" className="h-10 w-auto" />
          </div>

          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">
            {isRegister ? 'Crea tu cuenta' : 'Bienvenido de nuevo'}
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            {isRegister
              ? 'Regístrate para acceder a CNIB Connect. La membresía se activa tras la aprobación de tu solicitud.'
              : 'Ingresa con tu cuenta de socio o tu correo registrado.'}
          </p>

          <button
            onClick={loginWithGoogle}
            data-testid="google-login-button"
            className="mt-8 flex w-full items-center justify-center gap-3 rounded-full border border-slate-200 bg-white py-3.5 font-semibold text-slate-700 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
              <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
            </svg>
            Continuar con Google
          </button>

          <div className="my-6 flex items-center gap-4">
            <span className="h-px flex-1 bg-slate-200" />
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">o con tu correo</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-300 bg-white p-5" data-testid="register-avatar-section">
                <input ref={cameraRef} type="file" accept="image/*" capture="user" hidden onChange={(e) => uploadAvatar(e.target.files?.[0])} />
                <input ref={galleryRef} type="file" accept="image/*" hidden onChange={(e) => uploadAvatar(e.target.files?.[0])} />
                <div className="relative">
                  <div className={`flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-slate-100 ring-4 ${form.picture ? 'ring-emerald-400' : 'ring-orange-400/40'}`}>
                    {form.picture ? (
                      <img src={form.picture} alt="Avatar" className="h-full w-full object-cover" />
                    ) : uploadingAvatar ? (
                      <Loader2 className="h-8 w-8 animate-spin text-cnib-blue" />
                    ) : (
                      <User className="h-10 w-10 text-slate-400" />
                    )}
                  </div>
                  {form.picture && (
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, picture: '' }))}
                      className="absolute -right-1 -top-1 flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-white shadow"
                      data-testid="register-avatar-remove"
                      aria-label="Quitar foto"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <p className="text-center text-xs font-bold text-slate-700">Foto de perfil <span className="text-orange-500">*</span></p>
                <p className="text-center text-[11px] text-slate-500">Es obligatoria para activar tu red profesional CNIB.</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => cameraRef.current?.click()}
                    disabled={uploadingAvatar}
                    data-testid="register-avatar-camera"
                    className="inline-flex items-center gap-1.5 rounded-full bg-cnib-blue px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-cnib-blue/90 disabled:opacity-60"
                  >
                    <Camera className="h-3.5 w-3.5" />
                    Tomar foto
                  </button>
                  <button
                    type="button"
                    onClick={() => galleryRef.current?.click()}
                    disabled={uploadingAvatar}
                    data-testid="register-avatar-gallery"
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60"
                  >
                    <ImageIcon className="h-3.5 w-3.5" />
                    Galería
                  </button>
                </div>
              </div>
            )}
            {isRegister && (
              <div className="relative">
                <User className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input name="name" value={form.name} onChange={handleChange} required minLength={2} className={inputCls} placeholder="Nombre completo" data-testid="register-name-input" />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input type="email" name="email" value={form.email} onChange={handleChange} required className={inputCls} placeholder="Correo electrónico" data-testid="login-email-input" />
            </div>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input type="password" name="password" value={form.password} onChange={handleChange} required minLength={8} className={inputCls} placeholder={isRegister ? 'Contraseña (mínimo 8 caracteres)' : 'Contraseña'} data-testid="login-password-input" />
            </div>
            {isRegister && (
              <div className="grid gap-4 sm:grid-cols-2">
                <input name="whatsapp" value={form.whatsapp} onChange={handleChange} className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15" placeholder="WhatsApp (opcional)" data-testid="register-whatsapp-input" />
                <input name="city" value={form.city} onChange={handleChange} className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15" placeholder="Ciudad (opcional)" data-testid="register-city-input" />
              </div>
            )}
            <button
              type="submit"
              disabled={loading || (isRegister && !form.picture)}
              data-testid="login-submit-button"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-cnib-blue py-3.5 font-bold text-white shadow-lg shadow-cnib-blue/25 transition-all duration-300 hover:bg-cnib-bluehover disabled:opacity-60"
            >
              {loading && <Loader2 className="h-5 w-5 animate-spin" />}
              {loading ? 'Procesando…' : isRegister ? 'Crear cuenta' : 'Iniciar sesión'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600">
            {isRegister ? '¿Ya tienes cuenta?' : '¿Aún no tienes cuenta?'}{' '}
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="font-bold text-orange-500 transition-colors hover:text-orange-600"
              data-testid="auth-toggle-mode"
            >
              {isRegister ? 'Inicia sesión' : 'Regístrate'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
